import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { PropertyDto, PropertyMemberRole, PropertyMemberStatus, UserDto } from '@workspace/data-types';
import { AlertSnackBarService } from '@workspace/ng-aura';
import { UserStore } from '@workspace/ng-data-access';
import { EtxeEnumDisplayPipe } from '../../pipes/etxe-enum-display.pipe';
import { PropertyMissingInfoUtils } from '../../utils/property-missing-info.utils';
import { PropertyCharacteristicsDisplayComponent } from '../property-characteristics/main/display/characteristics-display.component';
import { PropertyConstructionDisplayComponent } from '../property-construction/display/construction-display.component';
import { PropertyDpeDisplayComponent } from '../property-dpe/main/display/dpe-display.component';
import { PropertyCadastralCardComponent } from '../property-geospatial';
import { PropertyGeospatialDisplayComponent } from '../property-geospatial/display/geospatial-display.component';
import { PropertyLocationDisplayComponent } from '../property-location/display/location-display.component';
import { PropertyMandateDisplayComponent } from '../property-mandate/display/mandate-display.component';
import { PropertyPhotosDisplayComponent } from '../property-photos/display/photos-display.component';
import { EtxePropertySellerInfoComponent } from '../seller-info/property-seller-info';
import { PropertyViewHeadlineComponent } from './components/property-view-headline/property-view-headline.component';
import { PropertyViewKeyCriteriaComponent } from './components/property-view-key-criteria/property-view-key-criteria.component';
import { PropertyViewSidebarComponent } from './components/property-view-sidebar/property-view-sidebar.component';

@Component({
  selector: 'etxe-property-view',
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    EtxeEnumDisplayPipe,
    PropertyPhotosDisplayComponent,
    PropertyLocationDisplayComponent,
    PropertyMandateDisplayComponent,
    PropertyDpeDisplayComponent,
    EtxePropertySellerInfoComponent,
    PropertyCharacteristicsDisplayComponent,
    PropertyConstructionDisplayComponent,
    PropertyGeospatialDisplayComponent,
    PropertyViewHeadlineComponent,
    PropertyViewKeyCriteriaComponent,
    PropertyViewSidebarComponent,
    PropertyCadastralCardComponent,
  ],
  templateUrl: './property-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EtxePropertyViewComponent {
  private readonly alertSnackbar = inject(AlertSnackBarService);
  private readonly userStore = inject(UserStore);

  // Property Input
  property = input.required<PropertyDto>();
  isOwnerView = input<boolean>(true);
  demoMode = input<boolean>(false);
  backLink = input<string>('/properties');
  backLabel = input<string>('Retour à mes annonces');
  showDefaultSidebar = input<boolean>(true);
  showMandateConditions = input<boolean>(true);
  hideSidebar = input<boolean>(false);
  showPhotos = input<boolean>(true);

  // Outputs
  propertyUpdated = output<PropertyDto>();
  previewAgentClicked = output<void>();

  // Missing Information Count for Sidebar
  readonly missingItemsCount = computed(() => {
    if (!this.isOwnerView() || this.demoMode()) return 0;
    return PropertyMissingInfoUtils.getAllMissingItems(this.property()).length;
  });

  // Current user is owner?
  readonly isCurrentUserOwner = computed<boolean>(() => {
    if (this.demoMode() && this.isOwnerView()) return true;
    const prop = this.property();
    const currentUserId = this.userStore.current()?._id;
    if (!currentUserId) return false;
    const propOwner = prop.owner || prop.createdBy;
    if (!propOwner) return false;
    const ownerId =
      typeof propOwner === 'object' && '_id' in propOwner ? (propOwner as UserDto)._id : propOwner;
    return ownerId?.toString() === currentUserId.toString();
  });

  // Current user is co-manager (accepted member with CO_OWNER role)?
  readonly isCurrentUserCoManager = computed<boolean>(() => {
    const currentUserId = this.userStore.current()?._id?.toString();
    const currentUserEmail = this.userStore.current()?.contactInfos?.email?.toLowerCase();
    if (!currentUserId && !currentUserEmail) return false;

    return (this.property().members || []).some((m) => {
      if (m.status !== PropertyMemberStatus.ACCEPTED) return false;
      if (m.role !== PropertyMemberRole.CO_OWNER) return false;
      const memberUserId =
        typeof m.user === 'object' && m.user && '_id' in m.user
          ? (m.user as UserDto)._id?.toString()
          : m.user?.toString();
      const memberEmail = m.email?.toLowerCase();
      return (
        (memberUserId && memberUserId === currentUserId) ||
        (memberEmail && currentUserEmail && memberEmail === currentUserEmail)
      );
    });
  });

  // Current user is viewer (accepted member with VIEWER role)?
  readonly isCurrentUserViewer = computed<boolean>(() => {
    const currentUserId = this.userStore.current()?._id?.toString();
    const currentUserEmail = this.userStore.current()?.contactInfos?.email?.toLowerCase();
    if (!currentUserId && !currentUserEmail) return false;

    return (this.property().members || []).some((m) => {
      if (m.status !== PropertyMemberStatus.ACCEPTED) return false;
      if (m.role !== PropertyMemberRole.VIEWER) return false;
      const memberUserId =
        typeof m.user === 'object' && m.user && '_id' in m.user
          ? (m.user as UserDto)._id?.toString()
          : m.user?.toString();
      const memberEmail = m.email?.toLowerCase();
      return (
        (memberUserId && memberUserId === currentUserId) ||
        (memberEmail && currentUserEmail && memberEmail === currentUserEmail)
      );
    });
  });

  shareListing(): void {
    if (navigator.clipboard) {
      void navigator.clipboard.writeText(window.location.href);
      this.alertSnackbar.success({
        title: 'Lien copié !',
        message: 'Le lien de l’annonce a été copié dans votre presse-papiers.',
      });
    }
  }
}
